/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react'
import { Dropdown as DropdownComponent } from 'react-bootstrap'
import './CallerDetailsStyle.css'
import { ICalls } from '@dataTypes/calls.type'
import { getFullAddress } from '@utils/address.util'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import { getFullName } from '@utils/fullname.util'
import { calculateAge } from '@utils/dob.utils'

interface CallerDetailsProps {
  call: ICalls
  onHide: () => void
}

const CallerDetails: React.FC<CallerDetailsProps> = ({ call, onHide }) => {
  const displayDetails = useMemo(() => {
    if (call.contact)
      return {
        photo:
          call.contact && call.contact.photo ? call.contact.photo : undefined,
        name: getFullName(call.contact),
        patientId: call.contact?.patient
          ? call.contact?.patient.public_id
          : call.contact?.provider
            ? call.contact?.provider.public_id
            : undefined,
        callTags: call.tags ?? [],
        birthDate: call.contact?.patient?.date_of_birth
          ? new Date(call.contact.patient.date_of_birth).toLocaleDateString(
              'en-US',
              { month: '2-digit', day: '2-digit', year: '2-digit' },
            )
          : 'N/A',
        age: call.contact?.patient?.date_of_birth
          ? calculateAge(call.contact?.patient?.date_of_birth)
          : undefined,
        gender: call.contact?.patient?.gender ?? 'N/A',
        cellphone: call.contact?.cellphone
          ? formatPhoneNumber(call.contact?.cellphone, false)
          : 'N/A',
        email: call.contact?.email?.length ? call.contact?.email : 'N/A',
        homePhone: call.contact?.home_phone
          ? formatPhoneNumber(call.contact?.home_phone)
          : 'N/A',
        workPhone: call.contact?.work_phone
          ? formatPhoneNumber(call.contact?.work_phone)
          : 'N/A',
        address: getFullAddress(call.contact),
      }
    else
      return {
        name: 'Unknown caller',
        address: 'N/A',
        homePhone: 'N/A',
        workPhone: 'N/A',
        cellphone:
          call.call_type === 'Incoming'
            ? call.caller_number
            : call.receiver_number,
        callTags: call.tags ?? [],
      }
  }, [call.contact])

  return (
    <div className="mc-calls-caller-details">
      <div className={'flex-none personal-details'}>
        <div className="patient-buttons">
          <img
            src={
              displayDetails.photo ??
              'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
            }
            alt={'patient-img'}
            height={80}
            width={80}
            className="caller-img"
          />
          <div className="cdc-main-details">
            <div className="name-and-buttons">
              <div className="cdc-name-header">
                <span className="caller-name">{displayDetails.name}</span>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-1">
              {displayDetails.callTags && displayDetails.callTags.length ? (
                displayDetails.callTags.map((tag: string) => (
                  <span
                    key={tag}
                    className={`status-item ${tag
                      ?.toLowerCase()
                      .replaceAll(' ', '-')}`}
                  >
                    {tag}
                  </span>
                ))
              ) : (
                <span className="status-item">N/A</span>
              )}
            </div>

            <div className="patient-action-buttons">
              <button
                type="button"
                className="unstyled-button icon-tray"
                aria-label="Call"
                onClick={() => {}}
              >
                <i className="ti ti-phone-filled" aria-hidden></i>
              </button>
              <button
                type="button"
                className="unstyled-button icon-tray"
                aria-label="Email"
                onClick={() => {}}
              >
                <i className="ti ti-mail-filled" aria-hidden></i>
              </button>
              <button
                type="button"
                className="unstyled-button icon-tray"
                aria-label="Message"
                onClick={() => {}}
              >
                <i className="ti ti-message-filled" aria-hidden></i>
              </button>
              <DropdownComponent style={{ marginBottom: '2px' }}>
                <DropdownComponent.Toggle
                  variant="light"
                  id="dropdown-patient-sidepanel"
                  className="patient-list-action"
                >
                  <i className="ti ti-dots-vertical"></i>
                </DropdownComponent.Toggle>
                <DropdownComponent.Menu className="dropdown-menu">
                  <DropdownComponent.Item onClick={() => {}}>
                    <i className="ti ti-calendar-check me-1"></i> Schedule
                    appointment
                  </DropdownComponent.Item>
                  <DropdownComponent.Item onClick={() => {}}>
                    <i className="ti ti-edit me-1"></i> Edit info
                  </DropdownComponent.Item>
                  <DropdownComponent.Item>
                    <i className="ti ti-exclamation-circle me-1"></i> Report
                    patient
                  </DropdownComponent.Item>
                  <DropdownComponent.Item onClick={() => {}}>
                    <i className="ti ti-trash me-1"></i> Delete account
                  </DropdownComponent.Item>
                </DropdownComponent.Menu>
              </DropdownComponent>
            </div>
          </div>
        </div>
        <button
          type="button"
          className="unstyled-button collapse-icon ti ti-layout-sidebar-right-collapse"
          aria-label="Hide caller details"
          onClick={onHide}
        />
      </div>
      <div className="cdc-secondary-details" style={{}}>
        <div className="cdc-sd-item">
          <span className="cdc-label">Email</span>
          <span className="cdc-details">{displayDetails.email}</span>
        </div>
        <div className="cdc-sd-item">
          <span className="cdc-label">Address</span>
          <span className="cdc-details">{displayDetails.address}</span>
        </div>
        <div className="cdc-sd-numbers">
          <div className="cdc-sd-item">
            <span className="cdc-label">Cellphone number</span>
            <span className="cdc-details">{displayDetails.cellphone}</span>
          </div>
          <div className="cdc-sd-item">
            <span className="cdc-label">Home number</span>
            <span className="cdc-details">{displayDetails.homePhone}</span>
          </div>
          <div className="cdc-sd-item">
            <span className="cdc-label">Work number</span>
            <span className="cdc-details">{displayDetails.workPhone}</span>
          </div>
          <div className="cdc-sd-item">
            <span className="cdc-label">Birthday</span>
            <span className="cdc-details">{displayDetails.birthDate}</span>
          </div>
        </div>
        <div className="cdc-sd-item">
          <span className="cdc-label">Gender</span>
          <span className="cdc-details">{displayDetails.gender}</span>
        </div>
      </div>
    </div>
  )
}

export default CallerDetails
